<script setup lang="ts">
import { ref, watch } from 'vue';
import type { FileNode } from '@/composables/use-sftp';
import { useWorkspaceContext } from '@/composables/use-workspace-context';
import { usePanelHandle } from '@/composables/use-panels';
import FileTree from '@/components/FileTree.vue';
import { SidebarMenuSkeleton } from '@/components/ui/sidebar';

const ctx = useWorkspaceContext();
const handle = usePanelHandle('Explorer');

// destructure refs to top-level bindings so Vue auto-unwraps .value in templates
const {
	rootNode,
	rootPath,
	activeFilePath,
	initializing,
	selectionState,
	toggleDirectory,
	openFile,
	changeRoot,
} = ctx;

const fileTreeRef = ref<InstanceType<typeof FileTree> | null>(null);

// sync initializing state to handle
watch(
	initializing,
	(loading) => {
		handle.loadProgress = loading ? Infinity : null;
	},
	{ immediate: true },
);

// focus the file tree container when the panel receives focus
handle.onFocus = () => {
	fileTreeRef.value?.focus();
};
</script>

<template>
	<div class="flex h-full flex-col overflow-y-auto p-2">
		<!-- loading state -->
		<div v-if="initializing" class="flex flex-col gap-1 p-2">
			<SidebarMenuSkeleton v-for="i in 8" :key="i" show-icon />
		</div>

		<!-- empty state -->
		<div
			v-else-if="!rootNode"
			class="flex flex-1 items-center justify-center px-4 py-3 text-sm text-muted-foreground"
		>
			no files
		</div>

		<!-- file tree -->
		<FileTree
			v-else
			ref="fileTreeRef"
			:root-node="rootNode"
			:root-path="rootPath"
			:active-file-path="activeFilePath"
			:selection-state="selectionState"
			@toggle="(n: FileNode) => toggleDirectory(n)"
			@open="(n: FileNode) => openFile(n)"
			@change-root="(p: string) => changeRoot(p)"
		/>
	</div>
</template>
